/*!
 * Griswold embed widget loader (v3 — iframe).
 *
 * Customer integration is unchanged from the legacy widget:
 *
 *   <script src=".../embed/v1/griswold-widget.js"
 *           data-chatbot="ask-thomas-v2"
 *           data-tenant="..."        (optional)
 *           async></script>
 *
 * (``data-api-base`` is accepted-but-ignored: the iframe location is always
 * derived from this script's own URL — see widgetBase below.)
 *
 * This script is intentionally a tiny vanilla-JS IIFE — no React, no
 * SDKs, no eval/Function. It just:
 *
 *   1. Reads the ``data-*`` attributes from its own ``<script>`` tag.
 *   2. Injects a fixed-position iframe pointing at
 *      ``<my-origin>/embed/v1/widget.html?chatbot=...&tenant=...``.
 *   3. Bridges postMessage events between the iframe (the React
 *      widget) and the host page:
 *        - ``widget:resize``     → adjusts iframe width/height.
 *        - ``widget:visibility`` → tracks open/closed (informational).
 *        - ``widget:navigate``   → opens URLs in a new tab on behalf of
 *          the iframe (which can't reliably call ``window.open`` for
 *          OAuth flows due to third-party cookie restrictions).
 *
 * The React widget inside the iframe owns its launcher bubble UI;
 * the loader only owns the iframe element + size.
 */

import { inEditorContext } from "./editorContext";
import { shouldSuppressLauncherForAppEmbed } from "./launcherSuppression";
import {
  normalizeHostOpenOptions,
  normalizeHostPageContext,
  type HostMessage,
  type HostOpenOptions,
  type HostPageContext,
} from "./bridge";

interface IframeMessage {
  type: string;
  width?: number;
  height?: number;
  open?: boolean;
  on?: boolean;
  url?: string;
  mode?: "tab" | "popup" | "host";
}

interface GriswoldHostApi {
  open(options?: HostOpenOptions): void;
  close(): void;
  setContext(context: HostPageContext | null): void;
}

declare global {
  interface Window {
    griswold?: GriswoldHostApi;
  }
}

(function () {
  "use strict";

  // ``document.currentScript`` is the right hook for inline ``<script>``
  // tags. It is null when the script is loaded as a module or via
  // dynamic import; in our integration path it's always non-null
  // because customers use the standard ``<script src=...>`` form.
  const script = document.currentScript as HTMLScriptElement | null;
  if (!script) return;
  const scriptEl = script;

  const chatbot = scriptEl.getAttribute("data-chatbot");
  const tenant = scriptEl.getAttribute("data-tenant");
  if (!chatbot) return;
  const chatbotSlug = chatbot;

  // Don't render the launcher inside a page builder / site editor. The
  // customer embeds this script in their page content, so it also runs
  // while they're editing that page in Divi's Visual Builder, the
  // WordPress block editor, etc. — where a floating chat bubble is just
  // noise in the editing canvas (and can overlap builder UI). Detect the
  // common editor contexts and bail before injecting anything. Detection
  // is best-effort and fail-safe: any error → render normally.
  if (inEditorContext()) return;

  // Derive the script's own origin (and any URL prefix like
  // ``/tr-ai``) so we can mount the iframe under the same path.
  // The customer's snippet points at ``.../tr-ai/embed/v1/griswold-widget.js``
  // on trbc.org; the iframe must live under the same prefix or
  // sibling assets won't resolve.
  const scriptUrl = new URL(scriptEl.src, window.location.href);
  // The widget iframe is ALWAYS a sibling of this loader script
  // (.../embed/v1/griswold-widget.js → .../embed/v1/widget.html), so the
  // iframe base is derived from the script's own URL. We deliberately do
  // NOT honor data-api-base here: it points at the API/public root (e.g.
  // ``/tr-ai``), which is missing the ``/embed/v1`` segment — using it as
  // the widget base loaded ``/tr-ai/widget.html`` (the SPA fallback / admin
  // app) instead of the real widget, rendering a broken box. The iframe's
  // React app talks to the API via its own build-time prefix, so it needs
  // no base override. The attribute is accepted-but-ignored for back-compat
  // with snippets that still include it.
  const widgetBase = scriptUrl.origin + scriptUrl.pathname.replace(
    /\/griswold-widget\.js$/,
    "",
  );
  const iframeOrigin = new URL(widgetBase, window.location.href).origin;

  const params = new URLSearchParams();
  params.set("chatbot", chatbotSlug);
  if (tenant) params.set("tenant", tenant);
  params.set("origin", window.location.origin);

  const iframeSrc = widgetBase + "/widget.html?" + params.toString();

  // Sizes used while the bubble is collapsed vs expanded. These can
  // be tweaked by ``widget:resize`` messages from the iframe. The
  // 110px footprint gives the launcher's drop-shadow room — at 84px
  // the soft blue glow was clipped on the iframe's right/bottom.
  // Collapsed footprint — wide enough for the labeled launcher pill
  // (icon + bot name). The pill is right-anchored and grows leftward; a
  // bare-icon launcher still fits. The iframe widens to the panel on open.
  const BUBBLE_W = "280px";
  const BUBBLE_H = "100px";
  const RESUME_MARKER_TTL_MS = 5 * 60 * 1000;
  const resumeAfterNavigation = consumeResumeAfterNavigation();
  // ``cssText`` sets all styles in one assignment — CSP-safe because
  // it's our own element. We position bottom-right; the iframe's
  // body owns the launcher button itself, so the iframe needs
  // enough room to draw the bubble plus its label.
  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "Support chat");
  iframe.setAttribute("aria-label", "Support chat widget");
  iframe.setAttribute("allow", "clipboard-write");
  iframe.setAttribute("loading", "lazy");
  iframe.src = iframeSrc;
  iframe.style.cssText = [
    "position:fixed",
    "right:0",
    "bottom:0",
    "width:" + BUBBLE_W,
    "height:" + BUBBLE_H,
    "border:0",
    "background:transparent",
    "color-scheme:light",
    "z-index:2147483646",
    "transition:width .25s ease, height .25s ease",
  ].join(";");

  let injectScheduled = false;
  function inject() {
    if (!document.body || document.readyState === "loading") {
      // Wait until the host markup is parsed before deciding whether to
      // mount. Full-page embeds may include a direct `mode=app` iframe after
      // the global script tag; injecting early would create a second launcher.
      if (!injectScheduled) {
        injectScheduled = true;
        document.addEventListener("DOMContentLoaded", inject, { once: true });
      }
      return;
    }
    if (iframe.isConnected) return;
    if (
      shouldSuppressLauncherForAppEmbed({
        script: scriptEl,
        chatbot: chatbotSlug,
        tenant,
        widgetBase,
      })
    ) {
      return;
    }
    document.body.appendChild(iframe);
  }
  inject();

  // ---------------- postMessage bridge ----------------

  let widgetReady = false;
  let currentContext: HostPageContext | null = null;
  const pendingHostMessages: HostMessage[] = [];

  function postToWidget(message: HostMessage): void {
    const target = iframe.contentWindow;
    if (!widgetReady || !target) {
      pendingHostMessages.push(message);
      return;
    }
    target.postMessage(message, iframeOrigin);
  }

  function flushHostMessages(): void {
    const target = iframe.contentWindow;
    if (!target) return;
    while (pendingHostMessages.length) {
      const message = pendingHostMessages.shift();
      if (message) target.postMessage(message, iframeOrigin);
    }
  }

  window.griswold = {
    open(options) {
      const normalized = normalizeHostOpenOptions(options);
      if (Object.prototype.hasOwnProperty.call(normalized, "context")) {
        currentContext = normalized.context ?? null;
      } else if (currentContext) {
        normalized.context = currentContext;
      }
      postToWidget({ type: "host:open", options: normalized });
    },
    close() {
      postToWidget({ type: "host:close" });
    },
    setContext(context) {
      currentContext = normalizeHostPageContext(context);
      postToWidget({ type: "host:context", context: currentContext });
    },
  };

  function isFromWidget(event: MessageEvent): boolean {
    if (event.source !== iframe.contentWindow) return false;
    // Same-origin messages have a meaningful ``origin`` field; we
    // accept only the iframe's own origin to defend against
    // host-page scripts impersonating the iframe.
    if (event.origin !== iframeOrigin) return false;
    return true;
  }

  let isFullscreen = false;

  function applyResize(width: number, height: number) {
    if (isFullscreen) return; // fullscreen owns sizing
    // Clamp to viewport so we don't draw off-screen on tiny mobile
    // browsers. The iframe page is responsible for picking a
    // reasonable preferred size; we just honor it within bounds.
    const vw = Math.max(280, window.innerWidth);
    const vh = Math.max(280, window.innerHeight);
    const w = Math.max(80, Math.min(width, vw));
    const h = Math.max(80, Math.min(height, vh));
    iframe.style.width = w + "px";
    iframe.style.height = h + "px";
    lastBubbleWidth = w + "px";
    lastBubbleHeight = h + "px";
  }

  /**
   * Open an OAuth/redirect URL in a centered popup window on
   * desktop, falling back to a new tab on small viewports where
   * popups feel cramped (and most mobile browsers ignore window
   * features anyway). The legacy widget used this same pattern; a
   * popup keeps the host page in the customer's eye instead of
   * stealing focus into a full browser tab.
   */
  function openAuthPopup(url: string): void {
    const POPUP_W = 520;
    const POPUP_H = 720;
    const vw = window.innerWidth || POPUP_W;
    const vh = window.innerHeight || POPUP_H;
    const usePopup = vw >= 720 && vh >= 600;
    if (!usePopup) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    const w = Math.min(POPUP_W, vw - 40);
    const h = Math.min(POPUP_H, vh - 40);
    // Multi-monitor friendly: window.screenX/Y are the host window's
    // top-left in screen coords, so centering against them places
    // the popup over the host instead of on screen 0.
    const screenX = typeof window.screenX === "number" ? window.screenX : 0;
    const screenY = typeof window.screenY === "number" ? window.screenY : 0;
    const left = Math.max(0, Math.floor(screenX + (vw - w) / 2));
    const top = Math.max(0, Math.floor(screenY + (vh - h) / 2));
    const features = [
      `width=${w}`,
      `height=${h}`,
      `left=${left}`,
      `top=${top}`,
      "popup=yes",
      "noopener",
      "noreferrer",
      "menubar=no",
      "toolbar=no",
      "location=yes",
      "status=no",
      "scrollbars=yes",
      "resizable=yes",
    ].join(",");
    const popup = window.open(url, "griswold_oauth", features);
    if (!popup) {
      // Popup blocked — fall back to a tab so the user still has
      // a path forward.
      window.open(url, "_blank", "noopener,noreferrer");
    }
  }

  function navigateHost(url: string): void {
    let target: URL;
    try {
      target = new URL(url, window.location.href);
    } catch {
      return;
    }
    if (target.protocol !== "http:" && target.protocol !== "https:") return;
    if (target.origin !== window.location.origin) {
      window.open(target.href, "_blank", "noopener,noreferrer");
      return;
    }
    markResumeAfterNavigation(target.href);
    applyFullscreen(false);
    iframe.style.width = BUBBLE_W;
    iframe.style.height = BUBBLE_H;
    lastBubbleWidth = BUBBLE_W;
    lastBubbleHeight = BUBBLE_H;
    window.location.assign(target.href);
  }

  function resumeMarkerKey(): string {
    return [
      "griswold",
      "resume-after-navigation",
      tenant || "default",
      chatbot,
    ].join(":");
  }

  function markResumeAfterNavigation(targetHref: string): void {
    try {
      window.sessionStorage.setItem(
        resumeMarkerKey(),
        JSON.stringify({
          target: targetHref,
          createdAt: Date.now(),
        }),
      );
    } catch {
      // Storage may be blocked by privacy settings. In that case the
      // next page simply opens fresh, which is safer than resuming on
      // every load.
    }
  }

  function consumeResumeAfterNavigation(): boolean {
    try {
      const key = resumeMarkerKey();
      const raw = window.sessionStorage.getItem(key);
      if (!raw) return false;
      window.sessionStorage.removeItem(key);
      const marker = JSON.parse(raw) as {
        target?: unknown;
        createdAt?: unknown;
      };
      if (typeof marker.createdAt !== "number") return false;
      if (Date.now() - marker.createdAt > RESUME_MARKER_TTL_MS) return false;
      if (typeof marker.target !== "string") return false;
      return urlsMatchExpectedNavigation(marker.target, window.location.href);
    } catch {
      return false;
    }
  }

  function urlsMatchExpectedNavigation(
    targetHref: string,
    currentHref: string,
  ): boolean {
    try {
      const current = new URL(currentHref);
      const target = new URL(targetHref, current.href);
      if (target.origin !== current.origin) return false;
      return canonicalPageUrl(target) === canonicalPageUrl(current);
    } catch {
      return false;
    }
  }

  function canonicalPageUrl(url: URL): string {
    let pathname = url.pathname;
    while (pathname.length > 1 && pathname.endsWith("/")) {
      pathname = pathname.slice(0, -1);
    }
    return `${url.origin}${pathname}${url.search}`;
  }

  // Remember the most recent non-fullscreen size so leaving fullscreen
  // can restore the right footprint immediately. Defaults to the
  // bubble — the iframe will widen to the panel only after the React
  // side sends ``widget:resize`` confirming the panel is open.
  let lastBubbleWidth = BUBBLE_W;
  let lastBubbleHeight = BUBBLE_H;

  function applyFullscreen(on: boolean) {
    const wasFullscreen = isFullscreen;
    isFullscreen = on;
    if (on) {
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.top = "0";
      iframe.style.left = "0";
      iframe.style.width = "100vw";
      // 100dvh tracks the *dynamic* viewport on iOS Safari / Android
      // Chrome — the on-screen browser chrome's auto-hide is taken
      // into account, so the iframe shrinks/grows with it instead of
      // running under the URL bar. ``100vh`` is the static fallback
      // for older browsers.
      iframe.style.height = "100vh";
      iframe.style.height = "100dvh";
      iframe.style.borderRadius = "0";
    } else {
      iframe.style.top = "";
      iframe.style.left = "";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.borderRadius = "";
      // Always reset back to the bubble footprint when leaving
      // fullscreen. The widget will follow up with a ``widget:resize``
      // if it's actually opening the panel inline — without this
      // reset the iframe stays glued to 100vw/100vh and the launcher
      // never reappears.
      if (wasFullscreen) {
        iframe.style.width = lastBubbleWidth;
        iframe.style.height = lastBubbleHeight;
      }
    }
  }

  window.addEventListener("message", (event: MessageEvent) => {
    if (!isFromWidget(event)) return;
    const data = event.data as IframeMessage | null;
    if (!data || typeof data !== "object") return;
    if (data.type === "widget:resize") {
      if (typeof data.width === "number" && typeof data.height === "number") {
        applyResize(data.width, data.height);
      }
      return;
    }
    if (data.type === "widget:visibility") {
      // The iframe owns its open/closed state; we just keep the size
      // in sync. The iframe also sends a ``widget:resize`` before
      // toggling so this is informational only.
      return;
    }
    if (data.type === "widget:fullscreen") {
      applyFullscreen(data.on === true);
      return;
    }
    if (data.type === "widget:navigate") {
      if (typeof data.url === "string" && /^https?:\/\//i.test(data.url)) {
        if (data.mode === "popup") {
          openAuthPopup(data.url);
        } else if (data.mode === "host") {
          navigateHost(data.url);
        } else {
          // Source links and any other plain navigation open in a new
          // tab so the host page (and the chat transcript) stay put.
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
      }
      return;
    }
    if (data.type === "widget:ready") {
      // The iframe just mounted its React app and is ready to receive
      // host messages. Re-send identity now in case the original
      // ``load``-event identity beat the iframe's message listener
      // (it usually does). The iframe is idempotent about identity.
      widgetReady = true;
      sendIdentity();
      flushHostMessages();
      window.dispatchEvent(new CustomEvent("griswold:ready"));
      return;
    }
  });

  // Tell the iframe about the host page. The iframe also posts
  // ``widget:ready`` after mount; we re-send identity then too so
  // the React app's listener never misses it.
  function sendIdentity() {
    const target = iframe.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        type: "host:identity",
        href: window.location.href,
        locale:
          (typeof navigator !== "undefined" && navigator.language) || "en",
        hostWidth: window.innerWidth,
        hostHeight: window.innerHeight,
        resumeAfterNavigation,
      },
      iframeOrigin,
    );
  }
  iframe.addEventListener("load", sendIdentity, { once: true });

  // Push the host's viewport on resize / orientation change so the
  // iframe can react (e.g. switch its own panel to fullscreen when
  // the host page is mobile-sized). Throttled with rAF to avoid
  // posting hundreds of messages during a drag-resize.
  let viewportRafScheduled = false;
  function postHostViewport() {
    viewportRafScheduled = false;
    const target = iframe.contentWindow;
    if (!target) return;
    target.postMessage(
      {
        type: "host:viewport",
        hostWidth: window.innerWidth,
        hostHeight: window.innerHeight,
      },
      iframeOrigin,
    );
  }
  window.addEventListener("resize", () => {
    if (viewportRafScheduled) return;
    viewportRafScheduled = true;
    requestAnimationFrame(postHostViewport);
  });
  window.addEventListener("orientationchange", postHostViewport);
})();
